import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
} from "antd";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

const STATUS_COLORS = {
  Confirmed:  { bg: "#ECFDF3", color: "#027A48" },
  Sent: { bg: "#EFF8FF", color: "#175CD3" },
  Pending:    { bg: "#FFFAEB", color: "#B54708" },
  Failed:     { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

// ── Component ──────────────────────────────────────────────────────────────
const ManageSettlement = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData || {};

  const [currentPage,  setCurrentPage]  = useState(1);
  const [selectedRow,  setSelectedRow]  = useState([]);
  const [searchTerm,   setSearchTerm]   = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading,      setLoading]      = useState(false);

  const pageSize = 10;

  const columns = [
    {
      title: "Settlement ID",
      dataIndex: "settlementId",
    },
    {
      title: "Trade ID",
      dataIndex: "tradeId",
    },
    {
      title: "Cash Amount",
      dataIndex: "cashAmount",
      render: (v, row) =>
        v != null
          ? `${row.cashCurrencyCode ?? ""} ${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`.trim()
          : "—",
    },
    {
      title: "Currency",
      dataIndex: "cashCurrencyCode",
      render: (v) => v ?? "—",
    },
    {
      title: "Settlement Method",
      dataIndex: "settlementMethodDescription",
      render: (v) => v ?? "—",
    },
    {
      title: "Status",
      dataIndex: "statusDescription",
      render: (val) => <StatusBadge status={val} />,
    },
  ];

  useEffect(() => {
    getSettlementList();
  }, []);

  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_SETTLEMENT")) {
      rightAction(cnt.RIGHT_ADD_BTN);
    }
  }, []);

  const getSettlementList = () => {
    setLoading(true);

    const body = { session };
    const url = `${apiConfig.GetSettlements}/settlements`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load settlements.",
        );
        setLoading(false);
        return;
      }

      const mapped = (res || []).map((el) => ({
        key: el.settlementId,
        settlementId: el.settlementId,
        tradeId: el.tradeId,
        cashAmount: el.cashAmount,
        cashCurrencyCode: el.cashCurrencyCode,
        settlementMethod: el.settlementMethod,
        settlementMethodDescription: el.settlementMethodDescription,
        status: el.status,
        statusDescription: el.statusDescription,
      }));

      setOriginalList(mapped);
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    });
  };

  const handleSearchInTable = (e) => {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        item.settlementMethodDescription?.toLowerCase().includes(q) ||
        item.statusDescription?.toLowerCase().includes(q) ||
        item.cashCurrencyCode?.toLowerCase().includes(q) ||
        String(item.tradeId).includes(q) ||
        String(item.settlementId).includes(q),
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_SETTLEMENT")) {
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      }
      return;
    }

    setSelectedRow(selectedRows);

    if (BOM.HasPermission?.("READ_SETTLEMENT")) {
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    }
    if (BOM.HasPermission?.("UPDATE_SETTLEMENT")) {
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
    }
  };

  const handleOnView = () => {
    if (rightBtn?.detailBtn === "primary") {
      navigate("/view-settlement", { state: selectedRow[0]?.settlementId });
    }
  };

  const handleOnCreate = () => {
    if (rightBtn?.addBtn === "primary") {
      navigate("/add-settlement");
    }
  };

  const handleOnEdit = () => {
    if (rightBtn?.editBtn === "primary") {
      navigate("/edit-settlement", { state: selectedRow[0]?.settlementId });
    }
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageSettlement}
    >
      <TopContent
        pageTitle={labelConfig.settlement}
        createPageTitle={labelConfig.settlementListing}
      />

      <Card
        style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}
      >
        <Spin spinning={loading}>
          <Row
            style={{
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={handleSearchInTable}
                  placeholder="Search settlements…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table
            rowSelection={{
              type: "checkbox",
              hideSelectAll: true,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              <ArrowLeft /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              showSizeChanger={false}
              itemRender={itemRender}
              onChange={setCurrentPage}
            />

            <Button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(ManageSettlement);